import { FC, Fragment } from 'react';
import { Redirect, Switch, useLocation, useRouteMatch } from 'react-router-dom';

import {
  BulkUploadContacts,
  BulkUploadContactsSage,
  BulkUploadCSVInvoices,
  BulkUploadInvoicesSage,
  CompanyRegistration,
  Contacts,
  Dashboard,
  IntegrationLogs,
  IntegrationSettings,
  InvoiceInner,
  Invoices,
  MyClients,
  NewClientXero,
  Prebook,
  Settings,
  SignUpSoleTrader,
  Transactions,
  Transfers,
} from 'pages';
import { PrivateRoute } from 'hoc';
import { useStoreState } from 'state';
import { ENTITY_TYPE } from 'types';
import CurrencyExchange from 'pages/CurrencyExchange/CurrencyExchange';
import SimpleTransfer from 'pages/SimpleTransfer/SimpleTransfer';
import Reports from 'pages/Reports/Reports';
import RiskSettings from 'pages/RiskSettings/RiskSettings';
import PrebooksOverview from 'pages/PrebooksOverview/PrebooksOverview';
import {
  platformNamesToLogsPaths,
  platformNamesToSettingsPaths,
} from './IntegrationsRouteUtil';
import PaymentRun from 'pages/PaymentRun/PaymentRun';
import PaymentRuns from 'pages/PaymentRuns/PaymentRuns';
import SidebarPageTemplate from 'components/pageTemplates/SidebarPageTemplate/SidebarPageTemplate';
import Risks from 'pages/Risks/Risks';

const SIDEBAR_PAGE_TEMPLATE_ROUTES = [
  '/app/invoices',
  '/app/risks',
  '/app/invoices/manual-upload',
  '/app/dashboard',
  '/app/prebook',
  '/app/transfers',
  '/app/settings',
  '/app/reports',
  '/app/contacts',
  '/app/currency-exchange',
  '/app/simple-transfer',
  '/app/simple-transfer',
  '/app/logs/xero',
  '/app/logs/sage',
  '/app/logs/quickbookssandbox',
  '/app/logs/quickbooks',
  '/app/logs/dynamics365',
  '/app/my-clients',
  '/app/transactions',
  '/app/payment-runs',
  '/app/invoices/prebookings',
  '/app/invoices/payment-run',
];

const isSidebarTemplatePage = (pathname: string) =>
  SIDEBAR_PAGE_TEMPLATE_ROUTES.includes(pathname);

const ApplicationRoutes: FC = () => {
  const { path } = useRouteMatch();
  const { pathname } = useLocation();
  const { entityType, isAccountant } = useStoreState(
    (state) => state.UserState
  );
  const { featureFlagById } = useStoreState((state) => state.FeatureFlagsState);

  const isCompany = entityType === ENTITY_TYPE.limitedCompany;
  const isSoleTrader = entityType === ENTITY_TYPE.soleTrader;

  const settingsPaths = Object.values(
    platformNamesToSettingsPaths
  ).map((settingPath) => settingPath.replace('/app', ''));

  const logsPaths = Object.values(platformNamesToLogsPaths).map((logPath) =>
    logPath.replace('/app', '')
  );

  const ApplicationRoutesWrapper = isSidebarTemplatePage(pathname)
    ? SidebarPageTemplate
    : Fragment;

  return (
    <ApplicationRoutesWrapper>
      <Switch>
        <PrivateRoute exact path={`${path}/dashboard`} component={Dashboard} />
        <PrivateRoute exact path={`${path}/prebook`} component={Prebook} />
        <PrivateRoute exact path={`${path}/transfers`} component={Transfers} />
        <PrivateRoute exact path={`${path}/settings`} component={Settings} />
        <PrivateRoute
          exact
          path={`${path}/risk-settings`}
          component={RiskSettings}
        />
        {featureFlagById('backtester') && (
          <PrivateRoute exact path={`${path}/reports`} component={Reports} />
        )}
        <PrivateRoute exact path={`${path}/contacts`} component={Contacts} />
        <PrivateRoute
          exact
          path={`${path}/currency-exchange`}
          component={CurrencyExchange}
        />
        <PrivateRoute
          exact
          path={`${path}/simple-transfer`}
          component={SimpleTransfer}
        />
        {settingsPaths.map((settingPath) => (
          <PrivateRoute
            key={settingPath}
            exact
            path={`${path}${settingPath}`}
            component={IntegrationSettings}
          />
        ))}

        {logsPaths.map((logPath) => (
          <PrivateRoute
            key={logPath}
            exact
            path={`${path}${logPath}`}
            component={IntegrationLogs}
          />
        ))}

        <PrivateRoute
          exact
          path={`${path}/bulk-contacts-sage`}
          component={BulkUploadContactsSage}
        />
        <PrivateRoute
          exact
          path={`${path}/bulk-contacts`}
          component={BulkUploadContacts}
        />
        <PrivateRoute
          exact
          path={`${path}/bulk-invoices-sage`}
          component={BulkUploadInvoicesSage}
        />

        <PrivateRoute
          exact
          path={`${path}/new-client/xero`}
          component={NewClientXero}
        />

        {isAccountant && (
          <PrivateRoute
            exact
            path={`${path}/my-clients`}
            component={MyClients}
          />
        )}

        <PrivateRoute exact path={`${path}/invoices`} component={Invoices} />

        <PrivateRoute
          exact
          path={`${path}/invoices/manual-upload`}
          component={BulkUploadCSVInvoices}
        />
        <PrivateRoute
          exact
          path={`${path}/transactions`}
          component={Transactions}
        />
        <PrivateRoute
          exact
          path={`${path}/payment-runs`}
          component={PaymentRuns}
        />
        <PrivateRoute
          exact
          path={`${path}/invoices/prebookings`}
          component={PrebooksOverview}
        />
        <PrivateRoute
          exact
          path={`${path}/invoices/payment-run`}
          component={PaymentRun}
        />
        <PrivateRoute
          exact
          path={`${path}/invoices/:invoiceId`}
          component={InvoiceInner}
        />

        {(isCompany || isSoleTrader) && (
          <PrivateRoute
            exact
            path={`${path}/company-registration`}
            component={isCompany ? CompanyRegistration : SignUpSoleTrader}
          />
        )}

        <PrivateRoute exact path={`${path}/risks`} component={Risks} />

        <Redirect to={`${path}/dashboard`} />
      </Switch>
    </ApplicationRoutesWrapper>
  );
};

export default ApplicationRoutes;
